import { useEffect, useState } from 'react';
import {
  Archive,
  Boxes,
  ChefHat,
  Clock3,
  Eye,
  Factory,
  History,
  Loader2,
  PackageCheck,
  PlayCircle,
} from 'lucide-react';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import SearchInputField from '../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import RestaurantRecipeProductionPanel from './RestaurantRecipeProductionPanel';
import ProductionBatchDetailModal from '../productionPlanner/ProductionBatchDetailModal';
import ProductionPlannerFilters from '../productionPlanner/ProductionPlannerFilters';
import ProductionResultPanel from '../productionPlanner/ProductionResultPanel';
import ProductionSuggestionCard from '../productionPlanner/ProductionSuggestionCard';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import useRestaurantProductionPlanner from '../../../hooks/restaurant/useRestaurantProductionPlanner';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import BranchService from '../../../services/branch/BranchService';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';
import RestaurantRecipeService from '../../../services/restaurant/RestaurantRecipeService';
import RestaurantMenuCategoryService from '../../../services/restaurant/RestaurantMenuCategoryService';
import { IBranchDropdown } from '../../../types/branches/branchInterfaces';
import { RestaurantRecipeDTO } from '../../../types/restaurant/RestaurantRecipeTypes';
import { RestaurantMenuCategoryDTO } from '../../../types/restaurant/RestaurantMenuTypes';
import { formatMoney } from '../../../utils/decimalValue';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';

type View = 'PLANNER' | 'HISTORY' | 'MANUAL';
const sourceLabel = {
  INVENTORY: 'Inventario',
  PURCHASE: 'Compra',
  INVENTORY_AND_PURCHASE: 'Inventario + compra',
} as const;

const RestaurantProductionScreen = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [view, setView] = useState<View>('PLANNER');
  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [recipes, setRecipes] = useState<RestaurantRecipeDTO[]>([]);
  const [recipeId, setRecipeId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [categories, setCategories] = useState<RestaurantMenuCategoryDTO[]>([]);
  const [plannerSearch, setPlannerSearch] = useState('');
  const [plannerCategoryId, setPlannerCategoryId] = useState(0);
  const [plannerPage, setPlannerPage] = useState(0);
  const [plannerSize, setPlannerSize] = useState(20);
  const [historySearch, setHistorySearch] = useState('');
  const [historySource, setHistorySource] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyPage, setHistoryPage] = useState(0);
  const [historySize, setHistorySize] = useState(20);
  const debouncedPlannerSearch = useDebouncedValue(plannerSearch, 350);
  const debouncedHistorySearch = useDebouncedValue(historySearch, 350);
  const { handle } = useApiErrorHandler();
  const planner = useRestaurantProductionPlanner(branchId);

  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);
        if (items.length) setBranchId(items[0].id);
      })
      .catch(handle)
      .finally(() => setLoadingBranches(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPickBranch]);

  useEffect(() => {
    if (!branchId) return;
    planner.reset();
    planner.loadOptions();
    setPlannerPage(0);
    setHistoryPage(0);
    setLoadingRecipes(true);
    setRecipeId('');
    setRecipes([]);
    RestaurantRecipeService.list(branchId, { active: true, page: 0, size: 100 })
      .then((response) => {
        const items = response.data?.content ?? [];
        setRecipes(items);
        if (items.length) setRecipeId(items[0].id);
      })
      .catch(handle)
      .finally(() => setLoadingRecipes(false));
    RestaurantMenuCategoryService.listFiltered(branchId, {
      page: 0,
      size: 100,
      search: null,
      active: true,
    }).then((response) => setCategories(response.content)).catch(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    if (!branchId || view !== 'HISTORY') return;
    void planner.loadHistory({
      page: historyPage,
      size: historySize,
      search: debouncedHistorySearch.trim() || undefined,
      source: historySource
        ? (historySource as keyof typeof sourceLabel)
        : undefined,
      status: historyStatus || undefined,
      dateFrom: historyDateFrom || undefined,
      dateTo: historyDateTo || undefined,
    });
  }, [branchId, debouncedHistorySearch, historyDateFrom, historyDateTo, historyPage, historySize, historySource, historyStatus, view]);

  useEffect(() => {
    if (!branchId || !planner.calculated) return;
    void planner.calculate({
      page: 0,
      size: plannerSize,
      search: debouncedPlannerSearch.trim() || undefined,
      categoryId: plannerCategoryId || undefined,
    });
  }, [debouncedPlannerSearch, plannerCategoryId]);

  const selectedRecipe = recipes.find((recipe) => recipe.id === recipeId);
  const producible = planner.suggestions.filter(
    (item) => item.producible
  ).length;
  const tabs: Array<{ value: View; label: string; icon: typeof Factory }> = [
    { value: 'PLANNER', label: 'Planificador', icon: Factory },
    { value: 'HISTORY', label: 'Historial de lotes', icon: History },
    { value: 'MANUAL', label: 'Producción manual', icon: ChefHat },
  ];

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />
      <SectionWrapper>
        <div className="overflow-hidden rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke p-5 dark:border-strokedark sm:px-7">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex items-start gap-3">
                <span className="rounded-xl bg-primary/10 p-3 text-primary">
                  <Factory className="h-6 w-6" />
                </span>
                <div>
                  <h1 className="text-xl font-semibold text-black dark:text-white">
                    Planificador de Producción
                  </h1>
                  <p className="mt-1 text-sm text-bodydark2">
                    Descubra qué puede fabricar, agrupe recetas y actualice
                    inventarios en un solo lote.
                  </p>
                </div>
              </div>
              <div className="w-full lg:w-80">
                {loadingBranches ? (
                  <div className="flex h-[74px] items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : canPickBranch ? (
                  <SelectField
                    label="Sucursal"
                    name="productionBranch"
                    value={branchId}
                    iconKey="building"
                    options={branches.map((branch) => ({
                      value: branch.id,
                      label: branch.name,
                    }))}
                    onChange={(event) =>
                      setBranchId(Number(event.target.value))
                    }
                  />
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 border-b border-stroke px-5 pt-4 dark:border-strokedark sm:px-7">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.value}
                  onClick={() => setView(tab.value)}
                  className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium ${
                    view === tab.value
                      ? 'border-primary text-primary'
                      : 'border-transparent text-bodydark2 hover:text-primary'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {view === 'PLANNER' && (
            <div className="space-y-5 p-5 sm:p-7">
              <ProductionPlannerFilters
                source={planner.source}
                purchaseId={planner.purchaseId}
                purchases={planner.purchases}
                loading={planner.loading}
                loadingOptions={planner.loadingOptions}
                search={plannerSearch}
                categoryId={plannerCategoryId}
                categories={categories}
                onSourceChange={(value) => { setPlannerPage(0); planner.changeSource(value); }}
                onPurchaseChange={(value) => { setPlannerPage(0); planner.changePurchase(value); }}
                onSearchChange={(value) => { setPlannerSearch(value); setPlannerPage(0); }}
                onCategoryChange={(value) => { setPlannerCategoryId(value); setPlannerPage(0); }}
                onCalculate={() => planner.calculate({
                  page: plannerPage,
                  size: plannerSize,
                  search: debouncedPlannerSearch.trim() || undefined,
                  categoryId: plannerCategoryId || undefined,
                })}
              />
              {planner.result && (
                <ProductionResultPanel
                  result={planner.result}
                  onClose={planner.clearResult}
                />
              )}
              {planner.calculated && (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="rounded-xl border border-stroke p-4 dark:border-strokedark">
                      <p className="flex items-center gap-2 text-xs font-semibold uppercase text-bodydark2">
                        <Boxes className="h-4 w-4 text-primary" />
                        Recetas evaluadas
                      </p>
                      <p className="mt-2 text-2xl font-bold text-black dark:text-white">
                        {planner.suggestions.length}
                      </p>
                    </div>
                    <div className="rounded-xl border border-stroke p-4 dark:border-strokedark">
                      <p className="flex items-center gap-2 text-xs font-semibold uppercase text-bodydark2">
                        <PackageCheck className="h-4 w-4 text-success" />
                        Disponibles
                      </p>
                      <p className="mt-2 text-2xl font-bold text-success">
                        {producible}
                      </p>
                    </div>
                    <div className="rounded-xl border border-stroke p-4 dark:border-strokedark">
                      <p className="flex items-center gap-2 text-xs font-semibold uppercase text-bodydark2">
                        <Archive className="h-4 w-4 text-warning" />
                        No disponibles
                      </p>
                      <p className="mt-2 text-2xl font-bold text-warning">
                        {planner.suggestions.length - producible}
                      </p>
                    </div>
                  </div>
                  {planner.suggestions.length ? (
                    <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                      {planner.suggestions.map((suggestion) => (
                        <ProductionSuggestionCard
                          key={suggestion.recipeId}
                          suggestion={suggestion}
                          quantity={
                            planner.quantities[suggestion.recipeId] ?? ''
                          }
                          onQuantityChange={(quantity) =>
                            planner.changeQuantity(
                              suggestion.recipeId,
                              quantity
                            )
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-stroke p-12 text-center dark:border-strokedark">
                      <Factory className="mx-auto h-10 w-10 text-bodydark2" />
                      <h3 className="mt-3 font-semibold text-black dark:text-white">
                        No hay recetas aptas para planificar
                      </h3>
                    </div>
                  )}
                  {planner.suggestionPage && (
                    <BasicPagination
                      currentPage={planner.suggestionPage.number}
                      totalPages={planner.suggestionPage.totalPages}
                      totalElements={planner.suggestionPage.totalElements}
                      size={planner.suggestionPage.size}
                      first={planner.suggestionPage.number === 0}
                      last={planner.suggestionPage.number >= planner.suggestionPage.totalPages - 1}
                      onPageChange={(value) => {
                        setPlannerPage(value);
                        void planner.calculate({
                          page: value,
                          size: plannerSize,
                          search: debouncedPlannerSearch.trim() || undefined,
                          categoryId: plannerCategoryId || undefined,
                        });
                      }}
                      onSizeChange={(value) => {
                        setPlannerSize(value);
                        setPlannerPage(0);
                        void planner.calculate({
                          page: 0,
                          size: value,
                          search: debouncedPlannerSearch.trim() || undefined,
                          categoryId: plannerCategoryId || undefined,
                        });
                      }}
                    />
                  )}
                  <div className="rounded-xl border border-stroke p-5 dark:border-strokedark">
                    <label className="mb-2 block font-medium text-black dark:text-white">
                      Notas del lote
                    </label>
                    <textarea
                      rows={2}
                      maxLength={500}
                      value={planner.notes}
                      onChange={(event) =>
                        planner.changeNotes(event.target.value)
                      }
                      className="w-full rounded border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary dark:border-strokedark"
                    />
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-bodydark2">
                          Costo estimado de la selección
                        </p>
                        <p className="text-xl font-bold text-black dark:text-white">
                          {formatMoney(planner.validation.estimatedTotalCost)}
                        </p>
                        {!planner.validation.valid &&
                          planner.validation.message && (
                            <p className="mt-1 text-sm text-danger">
                              {planner.validation.message}
                            </p>
                          )}
                        {planner.source !== 'INVENTORY' &&
                          planner.selectedPurchase?.status === 'DRAFT' && (
                            <p className="mt-1 text-sm text-warning">
                              Esta compra sirve para planificar, pero debe
                              confirmarse antes de producir.
                            </p>
                          )}
                      </div>
                      <button
                        data-testid="produce-batch-button"
                        onClick={planner.requestProduction}
                        disabled={
                          planner.producing ||
                          !planner.validation.valid ||
                          (planner.source !== 'INVENTORY' &&
                            planner.selectedPurchase?.status !== 'CONFIRMED')
                        }
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {planner.producing ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <PlayCircle className="h-5 w-5" />
                        )}
                        Confirmar producción
                      </button>
                    </div>
                  </div>
                </>
              )}
              {!planner.calculated && !planner.loading && (
                <div className="flex min-h-[260px] flex-col items-center justify-center rounded-xl border border-dashed border-stroke px-6 text-center dark:border-strokedark">
                  <Factory className="h-12 w-12 text-primary/40" />
                  <h3 className="mt-4 text-lg font-semibold text-black dark:text-white">
                    Prepare su siguiente lote
                  </h3>
                  <p className="mt-2 max-w-lg text-sm text-bodydark2">
                    Seleccione una fuente y calcule la capacidad real según
                    recetas, conversiones y existencias.
                  </p>
                </div>
              )}
            </div>
          )}

          {view === 'HISTORY' && (
            <div className="p-5 sm:p-7">
              <div className="mb-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5 xl:items-end">
                <div className="xl:pb-5">
                  <SearchInputField
                    label="Buscar"
                    name="productionHistorySearch"
                    value={historySearch}
                    iconKey="search"
                    placeholder="Lote, receta o producto..."
                    withMargin={false}
                    onChange={(event) => { setHistorySearch(event.target.value); setHistoryPage(0); }}
                  />
                </div>
                <SelectField
                  label="Origen"
                  name="productionHistorySource"
                  value={historySource}
                  placeholder="Todos los orígenes"
                  options={[
                    { value: '', label: 'Todos los orígenes' },
                    ...Object.entries(sourceLabel).map(([value, label]) => ({ value, label })),
                  ]}
                  onChange={(event) => { setHistorySource(String(event.target.value)); setHistoryPage(0); }}
                />
                <SelectField
                  label="Estado"
                  name="productionHistoryStatus"
                  value={historyStatus}
                  placeholder="Todos los estados"
                  options={[
                    { value: '', label: 'Todos los estados' },
                    { value: 'COMPLETED', label: 'Completado' },
                  ]}
                  onChange={(event) => { setHistoryStatus(String(event.target.value)); setHistoryPage(0); }}
                />
                <DatePicker label="Desde" name="productionHistoryFrom" value={historyDateFrom} iconKey="calendar" onChange={(value) => { setHistoryDateFrom(value); setHistoryPage(0); }} />
                <DatePicker label="Hasta" name="productionHistoryTo" value={historyDateTo} iconKey="calendar" onChange={(value) => { setHistoryDateTo(value); setHistoryPage(0); }} />
              </div>
              {planner.historyLoading ? (
                <div className="flex min-h-[280px] items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : planner.history.length === 0 ? (
                <div className="min-h-[260px] p-12 text-center text-bodydark2">
                  No hay lotes de producción registrados.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-stroke dark:border-strokedark">
                  <table className="w-full min-w-[850px] text-left text-sm">
                    <thead className="bg-gray-2 text-xs uppercase text-bodydark2 dark:bg-meta-4">
                      <tr>
                        <th className="p-4">Lote</th>
                        <th className="p-4">Fecha</th>
                        <th className="p-4">Fuente</th>
                        <th className="p-4">Productos</th>
                        <th className="p-4 text-right">Costo</th>
                        <th className="p-4 text-right">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stroke dark:divide-strokedark">
                      {planner.history.map((batch) => (
                        <tr key={batch.id}>
                          <td className="p-4 font-semibold text-primary">
                            #{batch.id}
                          </td>
                          <td className="p-4">
                            <span className="inline-flex items-center gap-2">
                              <Clock3 className="h-4 w-4 text-bodydark2" />
                              {new Date(batch.createdAt).toLocaleString(
                                'es-SV'
                              )}
                            </span>
                          </td>
                          <td className="p-4">
                            {sourceLabel[batch.source]}
                            {batch.purchaseNumber && (
                              <span className="block text-xs text-bodydark2">
                                {batch.purchaseNumber}
                              </span>
                            )}
                          </td>
                          <td className="p-4">
                            {batch.items.length} ·{' '}
                            {batch.items
                              .map((item) => item.productName)
                              .join(', ')}
                          </td>
                          <td className="p-4 text-right font-semibold">
                            {formatMoney(batch.totalCost)}
                          </td>
                          <td className="p-4 text-right">
                            <button
                              onClick={() => planner.viewBatch(batch)}
                              className="inline-flex items-center gap-2 rounded-lg border border-stroke px-3 py-2 text-primary hover:border-primary dark:border-strokedark"
                            >
                              <Eye className="h-4 w-4" />
                              Ver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {planner.historyPage && (
                <BasicPagination
                  currentPage={planner.historyPage.number}
                  totalPages={planner.historyPage.totalPages}
                  totalElements={planner.historyPage.totalElements}
                  size={planner.historyPage.size}
                  first={planner.historyPage.number === 0}
                  last={planner.historyPage.number >= planner.historyPage.totalPages - 1}
                  onPageChange={setHistoryPage}
                  onSizeChange={(value) => { setHistorySize(value); setHistoryPage(0); }}
                />
              )}
            </div>
          )}

          {view === 'MANUAL' && (
            <div>
              <div className="grid grid-cols-1 gap-x-3 border-b border-stroke p-5 dark:border-strokedark sm:grid-cols-2">
                <div className="flex items-center gap-3">
                  <ChefHat className="h-6 w-6 text-primary" />
                  <div>
                    <h2 className="font-semibold text-black dark:text-white">
                      Producción manual
                    </h2>
                    <p className="text-sm text-bodydark2">
                      Herramienta individual conservada para cálculos y consumos
                      controlados.
                    </p>
                  </div>
                </div>
                <SelectField
                  label="Receta"
                  name="productionRecipe"
                  value={recipeId}
                  iconKey="product"
                  disabled={loadingRecipes || !branchId}
                  options={recipes.map((recipe) => ({
                    value: recipe.id,
                    label: `${recipe.menuItemCode} · ${recipe.name}`,
                  }))}
                  onChange={(event) => setRecipeId(Number(event.target.value))}
                />
              </div>
              {loadingRecipes ? (
                <div className="flex min-h-[320px] items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : selectedRecipe && branchId ? (
                <div className="p-5">
                  <RestaurantRecipeProductionPanel
                    key={`${branchId}-${selectedRecipe.id}`}
                    branchId={branchId}
                    recipeId={selectedRecipe.id}
                    initialQuantity={selectedRecipe.yieldQuantity}
                    allowConsumption
                  />
                </div>
              ) : (
                <div className="min-h-[260px] p-12 text-center text-bodydark2">
                  No hay recetas activas disponibles en esta sucursal.
                </div>
              )}
            </div>
          )}
        </div>
        <ProductionBatchDetailModal
          batch={planner.selectedBatch}
          isOpen={planner.batchModalOpen}
          onClose={planner.closeBatch}
        />
      </SectionWrapper>
    </WorkspaceLayout>
  );
};

export default RestaurantProductionScreen;
